import { Component } from "preact";
import styles from "./styles.scss";
import AppBar from "../app-bar";
import AppContent from "../app-content";
import AppFooter from "../app-footer";
import {authenticate, fetchOrganization, fetchMessages} from "../../api";
import {
  CurrentUserContext,
  MessagesContext,
  OrganizationContext,
  RoomsContext,
  UsersStatusContext,
} from "../../contexts";
import socket, { MessageTypes } from "../../socket";
import FAB from "../fab";
import { parseISO, format } from "date-fns";
import {getRoom} from "../../utils";
import FCManager from "../../manager";
import PushNotificationsPrompt from "../../containers/PushNotificationsPrompt/PushNotificationsPrompt";
import OverlaySpinner from "../OverlaySpinner/OverlaySpinner";
import {NavigationContextProvider, withNavigation} from "../../contexts/NavigationContext";
import {GlobalContextProvider, withGlobals} from "../../contexts/GlobalContext";
import {AlertProvider, withAlert} from "../../contexts/AlertContext";
import ThemeContextProvider from "../../contexts/ThemeContext";
import {getProfile} from "../../selectors";

class App extends Component {
  static defaultProps = {
    options: {
      hideAppBar: false,
      displayMode: 'modal'
    }
  };

  constructor(props) {
    super(props);
    this.state = {
      currentUser: { },
      organization: { },
      messages: [],
      rooms: [],
      userStatus: {},
      isOpen: false,
      isFocused: false,
      subtitle: "",
      loading: true,
      error: false,
    };
  }

  componentDidUpdate(previousProps, previousState, snapshot) {
    if (previousProps.navigation.route !== this.props.navigation.route) {
      this.setState({ route: this.props.navigation.route });
    }
  }

  componentDidMount() {
    this.setState({route: this.props.navigation.route});
    // Do a POST request with the apiKey and user data
    // The POST should return info about the organization, such as all users
    // If the POST is successful, connect to websocket and display the widget
    // If not then keep it hidden and show an error in the logs
    const {
      apiKey,
      user,
    } = this.props.globals;

    authenticate(apiKey, user)
      .then(({ data }) => {
        const internalId = data.profile.internal_id;
        const profile = data.profile;
        this.props.globals.setOrganization(data);
        this.props.globals.setUser({...this.props.globals.user, ...profile, internalId});
        this.setState({
          organization: { ...data },
          currentUser: { internalId, name: profile.name, ...profile }
        });

        socket.attemptSubscribe({ organizationId: data.id, internalId: internalId }, {
          avatarUrl: profile.avatar_url,
          name: profile.name,
          bio: profile.bio
        });

        function handleVisibilityChange() {
          const status = document.hidden ? "offline" : "online";
          // don't send tell the system we are online if the chat button is closed
          if (!this.state.isOpen && status === "online")
            return;
          socket.emit(MessageTypes.UPDATE_STATUS, {
            userData: {
              status: `${status}:${new Date().toISOString()}`,
            }
          });
        }

        document.addEventListener(
          "visibilitychange",
          handleVisibilityChange.bind(this),
          false
        );

        window.addEventListener(
          "beforeunload",
          () => {
            const status = "offline";
            if (!this.state.isOpen && status === "online")
              return;
            socket.emit(MessageTypes.UPDATE_STATUS, {
              userData: {
                status: `${status}:${new Date().toISOString()}`
              }
            });
          }
        );

        socket.on(MessageTypes.SYSTEM_ALERT, body => {
          if (body.type === "another_connection_already_subscribed") {
            console.log("another device is connected");
            this.setState({isOpen: true, loading: false, error: true});
            this.showAlert("Parece que você já está conectado em outro dispositivo.", "Não é possível se conectar ao chat em 2 dispositivos diferentes ao mesmo tempo. Por favor, feche outras abas ou desconecte outros dispositivos que possam estar conectados e atualize a página.", {hideDismiss: true});
          }
        });

        socket.on(MessageTypes.JOINED_ROOM, body => {
          const rooms = [...this.state.rooms.filter(room => room && room.id !== body.id), body];
          this.setState({rooms});
          // As soon as we receive a room, if we are in chat, we update the param
          if (this.state.route.path === '/chat' || this.state.route.path === '/video-call-lobby') {
            const { roomId, contactInternalId, channelName } = this.state.route.params;
            const computedRoom = getRoom(rooms, roomId, contactInternalId, channelName);
            if (computedRoom) {
              const route = {...this.state.route, params: {...this.state.route.params, roomId: computedRoom.id}};
              this.props.navigation.navigate(route);
            }
          }
          // If we don't know this user from earlier, we need to fetch all users again
          const room = body;
          if (room.type === "conversation" && room.usersInternalIds) {
            const otherUser = getProfile(this.state.organization, room.usersInternalIds.find(id => id !== this.state.currentUser.internalId));
            if (!otherUser) {
              this.updateOrganization();
            }
          }
        });

        socket.on(MessageTypes.MESSAGE_RECEIVED, body => {
          this.setState({messages: [...this.state.messages, body.message]});
          if (this.state.route.path === '/chat' || (this.state.route.path === '/video-call-lobby' && this.state.route.params.isChatOpen)) {
            const { roomId, contactInternalId, channelName } = this.state.route.params;
            const computedRoom = getRoom(this.state.rooms, roomId, contactInternalId, channelName);
            if (computedRoom && computedRoom.id === body.message.roomId && computedRoom.type === "conversation") {
              // when we do this we mark messages we received as read
              socket.emit(MessageTypes.UPDATE_STATUS, {
                userData: {
                  focusedRoom: body.message.roomId
                }
              });
            }
          }
        });

        // TODO: move the message name to flic-chat-js-sdk
        socket.on('messages_updated', (body) => {
          const messages = this.state.messages;
          const updatedMessages = body.messages;
          for (const update of updatedMessages) {
            const idx = messages.findIndex(m => m.id === update.id);
            messages[idx] = update;
          }
          this.setState({messages: [...messages]});
        });

        socket.on(MessageTypes.SYSTEM_ALERT, body => {
          if (body.type === 'warning_too_many_messages')
            this.showAlert('Atenção', 'Você está enviando mensagens demais. Aguarde alguns instantes para enviar novamente.');
          if (body.type === 'warning_user_silenced' && body.data.internalId === this.state.currentUser.internalId) {
            if (body.data.minutes > 0)
              this.showAlert('Você foi silenciado por um moderador', `Você precisa aguardar ${body.data.minutes} minutos para enviar mensagens novamente.`);
            else this.showAlert('Um moderador removeu o seu silêncio', 'Você já pode enviar mensagens novamente.');
          }
          if (body.type === 'warning_room_settings_changed') {
            // Check if we are in chat and the room that changed is the current user room
            const roomId = body.data.roomId;
            const { path, params } = this.state.route;
            if (path === '/chat' && typeof body.data.update.restrictedUntil === "string") {
              const computedRoom = getRoom(this.state.rooms, params.roomId, params.contactInternalId, params.channelName);
              if (computedRoom && computedRoom.id === roomId) {
                const message = body.data.update.restrictedUntil ? 'Somente moderadores podem enviar mensagens para a sala.' : 'Você agora pode enviar mensagens para a sala.';
                this.showAlert('Um moderador alterou as configurações da sala', message);
              }
            }
          }
        });

        socket.on(MessageTypes.RESTORE_MESSAGES, body => {
          let rooms = this.state.rooms;
          let messages = this.state.messages;
          body.messages.forEach(message => {
            messages = [...messages.filter(m => m.id !== message.id), message];
          });
          messages.sort(sortBySentAt);

          const bodyRooms = body.rooms || [];

          this.setState({
            rooms: [...rooms.filter(room => !bodyRooms.find(r => r.id === room.id)), ...body.rooms],
            messages
          });

          if (FCManager.pendingCommands.length > 0)
            FCManager.pendingCommands.forEach(this.handleCommandAdded);

          // This is a special case where we might receive a command to open a direct conversation, and we already
          // have a room, so we must update the roomId param in the currentRoute
          // We use a setTimeout to make sure the this.state.route is updated
          setTimeout(() => {
            const currentRoute = this.state.route;
            if (currentRoute && currentRoute.path === "/chat" && currentRoute.params.contactInternalId) {
              const contactInternalId = currentRoute.params.contactInternalId;
              const computedRoom = getRoom(this.state.rooms, "", contactInternalId, "");
              if (computedRoom) {
                this.props.navigation.navigate({path: "/chat", params: {...currentRoute.params, roomId: computedRoom.id}});
              }
            }
          }, 10);

          FCManager.addEventListener(this.handleCommandAdded);
          this.setState({loading: false});

          if (!this.state.isOpen && this.props.globals.options.displayMode === "expand-in-container") {
            this.setState({isOpen: true});
          }
        });

        socket.on(MessageTypes.ORGANIZATION_UPDATED, () => {
          fetchOrganization(apiKey, this.state.organization.id).then(
            ({ data }) => {
              this.setState({ organization: { ...data } });
            }
          );
        });

        socket.on(MessageTypes.ROOM_UPDATED, body => {
          if (body.type === 'room_settings_changed') {
            const room = body.room;
            this.setState({rooms: [...this.state.rooms.filter(r => r && r.id !== room.id), room]});
          }
        });

        socket.on(
          MessageTypes.USER_STATUS_UPDATED,
          (body) => {
            const upd = {...this.state.userStatus, [body.internalId]: body};
            this.setState({userStatus: upd});
            if (this.state.route.path === "/chat") {
              const { roomId, contactInternalId, channelName } = this.state.route.params;
              const { currentUser } = this.state;
              const { rooms } = this.state;
              const computedRoom = getRoom(rooms, roomId, contactInternalId, channelName);
              if (channelName) {
                // do nothing, its a channel, we don't care, we might not have received the room info yet
                return;
              }
              // its a direct conversation
              const isInRoom = (computedRoom && computedRoom.usersInternalIds.includes(body.internalId)) || contactInternalId === body.internalId;
              const isOtherUserInChat = isInRoom && (body.internalId !== currentUser.internalId || contactInternalId === body.internalId);
              if (isOtherUserInChat) {
                if (body.streamingSince) {
                  this.setState({subtitle: 'na chamada de vídeo'});
                }
                else if (body.status.startsWith('online')) {
                  this.setState({ subtitle: 'online' });
                } else if (body.status.startsWith('offline')) {
                  const time = parseISO(body.status.replace("offline:", ""));
                  this.setState({ subtitle: "visto por último às " + format(time, "HH:mm") });
                }
              }
            } else if (this.state.route.path === "/video-call-lobby") {
              const { roomId, contactInternalId, channelName } = this.state.route.params;
              const { currentUser } = this.state;
              const { rooms } = this.state;
              const computedRoom = getRoom(rooms, roomId, contactInternalId, channelName);
              const isInRoom = (computedRoom && computedRoom.usersInternalIds.includes(body.internalId)) || contactInternalId === body.internalId;
              const isOtherUserInChat = isInRoom && (body.internalId !== currentUser.internalId || contactInternalId === body.internalId);
              if (isOtherUserInChat) {
                this.props.navigation.navigate({...this.state.route, params: {
                  ...this.state.route.params,
                    partnerStreamingSince: body.streamingSince
                }});
              }
            }
          }
        );

        socket.addEventListener(({isSubscribed, isConnected}) => {
          if (!isSubscribed && isConnected) {
            socket.attemptSubscribe({ organizationId: data.id, internalId: internalId }, {
              avatarUrl: profile.avatar_url,
              name: profile.name,
              bio: profile.bio
            });
          }
        });

        // Setup OneSignal
        const script = document.createElement("script");
        script.setAttribute("src", "https://cdn.onesignal.com/sdks/OneSignalSDK.js");
        document.body.appendChild(script);

        window.OneSignal = window.OneSignal || [];
        window.OneSignal.push(() => {
          window.OneSignal.init({
            appId: data.onesignal_appid,
            allowLocalhostAsSecureOrigin: true,
            notificationClickHandlerMatch: 'origin',
            notificationClickHandlerAction: 'focus',
            welcomeNotification: {
              title: 'Notificações de chat habilitadas',
              message: 'Você receberá uma notificação como essa sempre que receber uma mensagem.'
            }
          });
        });
        window.OneSignal.push(() => {
          const updateExternalUserId = () => {
            if (window.OneSignal.isPushNotificationsEnabled) {
              window.OneSignal.isPushNotificationsEnabled(function(isEnabled) {
                if (isEnabled) window.OneSignal.setExternalUserId(internalId);
              });
            }
          };

          window.OneSignal.push(() => {
            const onNotificationClicked = data => {
              this.setOpen(true);
              this.props.navigation.navigate({
                path: "/chat",
                params: { roomId: data.data.roomId }
              });
            };

            const handler = function(data) {
              // call your primary handler
              onNotificationClicked(data);

              // add another handler right away
              window.OneSignal.push(function() {
                window.OneSignal.addListenerForNotificationOpened(handler);
              });
            };

            // attach your handler for the first time
            window.OneSignal.addListenerForNotificationOpened(handler);
          });

          updateExternalUserId();

          window.OneSignal.on("subscriptionChange", function() {
            updateExternalUserId();
          });
        });
      })
      .catch(err => {
        console.error("Error while authenticating with the chat interface:");
        console.error(err);
      });
  }

  updateOrganization = () => {
    return new Promise((resolve) => {
      fetchOrganization(this.props.globals.apiKey, this.state.organization.id).then(
        ({ data }) => {
          this.setState({ organization: { ...data } }, () => {
            resolve(this.state.organization);
          });
        }
      );
    });
  };

  handleCommandAdded = command => {
    FCManager.executeCommand(command, () => {
      if (command.name === 'open_conversation') {
        this.props.navigation.navigate({
          path: "/chat",
          params: {
            contactInternalId: command.params.contactInternalId
          }
        });
        this.setOpen(true);
      } else if (command.name === 'open_channel') {
        this.props.navigation.navigate({
          path: "/chat",
          params: {
            channelName: command.params.channelName
          }
        });
        socket.emit(MessageTypes.ENTER_CHANNEL, {
          channelName: command.params.channelName,
        });
        this.setOpen(true);
      } else if (command.name === 'open_video_call') {
        this.props.navigation.navigate({
          path: "/video-call-lobby",
          params: {
            contactInternalId: command.params.contactInternalId,
          }
        });
        this.setOpen(true);
      }
    });
  };

  componentWillUnmount() {
    FCManager.removeEventListener(this.handleCommandAdded);
  }

  setOpen = isOpen => {
    this.setState({ isOpen });
    const status = isOpen ? "online" : "offline";
    socket.emit(MessageTypes.UPDATE_STATUS, {
      userData: {
        status: `${status}:${new Date().toISOString()}`
      }
    });
  };

  showAlert = (title, message, options) => {
    this.props.alert.showAlert(title, message, options);
  };

  fetchMoreMessages = roomId => {
    return new Promise((resolve) => {
      const messages = this.state.messages.filter(m => m.roomId === roomId);
      if (messages.length > 0) {
        fetchMessages(roomId, messages.length, messages.length + 50).then(response => {
          const messages = response.data.messages;
          resolve(response.data);
          this.setState({messages: [...messages, ...this.state.messages].sort(sortBySentAt)});
        });
      }
    });
  };

  render() {
    const {
      organization,
      currentUser,
      messages,
      rooms,
      userStatus,
      isOpen,
      loading,
      error
    } = this.state;
    const { options } = this.props.globals;

    let chatRootClasses = [styles.chatRoot];
    let appContainerClasses = [styles.appContainer];
    if (options.displayMode === 'expand-in-container') {
      chatRootClasses = [...chatRootClasses, styles.chatRootExpandInContainer];
      appContainerClasses = [...appContainerClasses, styles.appContainerExpandInContainer];
    }
    return (
      <div className={chatRootClasses.join(" ")}>
        <OrganizationContext.Provider value={organization}>
          <CurrentUserContext.Provider value={currentUser}>
            <RoomsContext.Provider value={rooms}>
              <UsersStatusContext.Provider value={userStatus}>
                <MessagesContext.Provider value={messages}>
                  {!loading && options.displayMode === 'modal' && (
                    <FAB
                      onClick={() => this.setOpen(!isOpen)}
                      className={styles.fab}
                    />
                  )}
                  {isOpen && (
                    <div className={appContainerClasses.join(" ")}>
                      {!options.hideAppBar && !error && <AppBar
                        onRequestClose={() => this.setOpen(!isOpen)}
                        subtitle={this.state.subtitle}
                      />}
                      {/* TODO: actually add a field to the organization to control this setting */}
                      {organization && organization.id !== 1 && <PushNotificationsPrompt/>}
                      {!error && <AppContent
                        showAlert={this.showAlert}
                        onRequestMoreMessages={this.fetchMoreMessages}
                        updateOrganization={this.updateOrganization}
                      />}
                      {!error && <AppFooter />}
                      {loading && <OverlaySpinner />}
                    </div>
                  )}
                </MessagesContext.Provider>
              </UsersStatusContext.Provider>
            </RoomsContext.Provider>
          </CurrentUserContext.Provider>
        </OrganizationContext.Provider>
      </div>
    );
  }
}

function sortBySentAt(a, b) {
  if (a.sentAt < b.sentAt)
    return -1;
  if (a.sentAt > b.sentAt)
    return 1;
  return 0;
}

const Wrapper = props => <GlobalContextProvider {...props}>
  <ThemeContextProvider variables={(props.options || {}).variables}>
    <NavigationContextProvider>
      <AlertProvider>
        {withGlobals(withNavigation(withAlert(App)))()}
      </AlertProvider>
    </NavigationContextProvider>
  </ThemeContextProvider>
</GlobalContextProvider>;

export default Wrapper;



// WEBPACK FOOTER //
// ./components/app/index.js